/**
 * Permission System — Context-Aware Role Policies
 *
 * Replaces the simple boolean permission map with a policy-based system
 * that can evaluate permissions based on account relationships and context.
 *
 * Created as part of Account System Refactor — Fase 2, stap 2.11 (s83)
 *
 * Usage:
 *   canPerform(account, 'manage_classroom', { accounts, targetClassroomId: 'class_123' })
 *   canPerform(account, 'practice') // no context needed for simple permissions
 */

import type { Account, AccountRole, AccountsState } from '../types'
import { getLinkedLearners } from './queries'
import { getTeacherClassrooms } from './classrooms'

// ── Types ────────────────────────────────────────────────────

/**
 * All permission actions in the system.
 * Grouped by domain for clarity.
 */
export type PermissionAction =
  // Learner actions
  | 'practice'
  | 'view_own_progress'
  | 'join_classroom'
  | 'view_own_data'
  // Parent actions
  | 'view_child_progress'
  | 'link_child'
  | 'export_child_data'
  // Teacher actions
  | 'manage_classroom'
  | 'create_classroom'
  | 'create_student'
  | 'view_learner'
  | 'remove_learner'
  | 'export_class_data'
  // Admin actions
  | 'manage_accounts'
  | 'view_all_data'
  | 'manage_governance'
  | 'developer_tools'
  // Wildcard
  | '*'

/**
 * Context for evaluating conditional permissions.
 * Only provide the fields relevant to the permission being checked.
 */
export interface PermissionContext {
  accounts?: AccountsState
  targetLearnerId?: string
  targetClassroomId?: string
  targetAccountId?: string
}

interface PolicyRule {
  action: PermissionAction
  condition: (account: Account, ctx?: PermissionContext) => boolean
}

// ── Policy Definitions ───────────────────────────────────────

/**
 * Check if a teacher owns a specific classroom.
 */
function isClassroomOwner(account: Account, ctx?: PermissionContext): boolean {
  if (!ctx?.accounts || !ctx.targetClassroomId) return false
  if (account.role !== 'teacher') return false

  const classrooms = getTeacherClassrooms(ctx.accounts, account.id)
  return classrooms.some((c) => c.id === ctx.targetClassroomId)
}

/**
 * Check if an account has a relationship with a target learner.
 */
function isLinkedToLearner(account: Account, ctx?: PermissionContext): boolean {
  if (!ctx?.accounts || !ctx.targetLearnerId) return false
  if (account.role !== 'teacher' && account.role !== 'parent') return false

  const linked = getLinkedLearners(ctx.accounts, account.id)
  return linked.some((l) => l.id === ctx.targetLearnerId)
}

const ROLE_POLICIES: Record<AccountRole, PolicyRule[]> = {
  learner: [
    { action: 'practice', condition: () => true },
    { action: 'view_own_progress', condition: () => true },
    { action: 'view_own_data', condition: () => true },
    { action: 'join_classroom', condition: () => true },
  ],
  parent: [
    { action: 'view_child_progress', condition: isLinkedToLearner },
    { action: 'link_child', condition: () => true },
    { action: 'export_child_data', condition: isLinkedToLearner },
  ],
  teacher: [
    { action: 'manage_classroom', condition: isClassroomOwner },
    { action: 'create_classroom', condition: () => true },
    { action: 'create_student', condition: () => true },
    { action: 'view_learner', condition: isLinkedToLearner },
    { action: 'remove_learner', condition: isLinkedToLearner },
    { action: 'export_class_data', condition: isClassroomOwner },
  ],
  admin: [{ action: '*', condition: () => true }],
}

// ── Public API ───────────────────────────────────────────────

/**
 * Check if an account can perform a specific action.
 *
 * Demo accounts bypass all permission checks (always allowed).
 * Admin accounts have wildcard access to everything.
 *
 * @param account - The account attempting the action
 * @param action - The permission action to check
 * @param context - Optional context for conditional permissions
 * @returns true if the action is allowed
 *
 * @example
 * // Simple check (no context needed)
 * canPerform(learnerAccount, 'practice') // → true
 * canPerform(learnerAccount, 'manage_classroom') // → false
 *
 * @example
 * // Context-aware check
 * canPerform(teacherAccount, 'manage_classroom', {
 *   accounts: state,
 *   targetClassroomId: 'class_abc'
 * }) // → true only if teacher owns that classroom
 *
 * @example
 * // Parent viewing child
 * canPerform(parentAccount, 'view_child_progress', {
 *   accounts: state,
 *   targetLearnerId: 'learner_123'
 * }) // → true only if parent is linked to that learner
 */
export function canPerform(
  account: Account,
  action: PermissionAction,
  context?: PermissionContext,
): boolean {
  // Demo accounts bypass all checks
  if (account.isDemo) return true

  const policies = ROLE_POLICIES[account.role] || []

  // Check for wildcard (admin)
  const wildcard = policies.find((p) => p.action === '*')
  if (wildcard) return wildcard.condition(account, context)

  // Check for specific action
  const match = policies.find((p) => p.action === action)
  return match ? match.condition(account, context) : false
}

/**
 * Get all actions an account role is allowed to perform (without context).
 * Useful for UI: show/hide features based on role.
 */
export function getAllowedActions(role: AccountRole): PermissionAction[] {
  const policies = ROLE_POLICIES[role] || []

  // Admin has wildcard — return all actions
  if (policies.some((p) => p.action === '*')) {
    return Object.values(ROLE_POLICIES)
      .flat()
      .map((p) => p.action)
      .filter((a) => a !== '*')
  }

  return policies.map((p) => p.action)
}

/**
 * Check if a role has a specific permission (without context).
 * Simpler version for UI gating where context isn't available.
 */
export function roleHasPermission(
  role: AccountRole,
  action: PermissionAction,
): boolean {
  const policies = ROLE_POLICIES[role] || []
  if (policies.some((p) => p.action === '*')) return true
  return policies.some((p) => p.action === action)
}
